import type { QueryClient } from '@tanstack/react-query';
import type { Roteador } from '@/app/router';
import type { ErroDaApi } from '@/lib/api/erros';
import { meChaves } from './dados/me';

/**
 * Tratamento central do 401 (CA-20; docs/02): limpa o cache e leva ao login com o aviso de sessão
 * expirada, voltando depois para onde a pessoa estava.
 *
 * Só age se havia sessão nesta aba (o `/me` está no cache). Isso ignora o 401 esperado de quem
 * ainda não entrou e evita o laço: depois do `clear()`, os outros 401 que chegarem juntos não
 * encontram mais o `/me`.
 */
export function criarAoNaoAutenticado(queryClient: QueryClient, roteador: Roteador) {
  return () => {
    if (queryClient.getQueryData(meChaves.me) === undefined) return;
    queryClient.clear();
    const { href, pathname } = roteador.state.location;
    void roteador.navigate({
      to: '/entrar',
      search: { aviso: 'sessao-expirada', ...(pathname === '/' ? {} : { volta: href }) },
    });
  };
}

/** Motivos de 403 que mudam o que o `/me` diz da sessão (backend: requireOrganization, RN-010). */
const MOTIVOS_DA_SESSAO: ReadonlySet<string> = new Set([
  'ACESSO_BLOQUEADO',
  'SEM_EMPRESA_ATIVA',
  'DOIS_FATORES_OBRIGATORIO',
]);

/**
 * Tratamento central do 403 (spec 003, CA-03): se o motivo é da sessão (vínculo bloqueado ou
 * removido, 2FA desligado), relê o `/me` para o layout mostrar a tela certa (bloqueado, escolher a
 * academia). Um 403 do papel não muda nada: a tela mostra "Você não tem acesso a esta tela.".
 */
export function criarAoSemPermissao(queryClient: QueryClient) {
  return (erro: ErroDaApi) => {
    if (erro.motivo === undefined || !MOTIVOS_DA_SESSAO.has(erro.motivo)) return;
    void queryClient.invalidateQueries({ queryKey: meChaves.me });
  };
}
